<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { RouterLink } from "vue-router";

import type { Master } from "@/entities/master";
import {
  Button,
  FormAlert,
  Input,
  Label,
  PageCTA,
  Skeleton,
} from "@/shared/ui";

import type { FieldKey } from "../model/useMasterProfileForm";
import { useMasterProfileForm } from "../model/useMasterProfileForm";

/**
 * Форма профілю майстра (spec §4.3/§8.2), спільна для create/edit. Тримає
 * composable `useMasterProfileForm`; навігацію НЕ робить — емітить події, роутинг
 * вирішує сторінка (§6). Картка (Card) — відповідальність споживача.
 */
const props = defineProps<{
  mode: "create" | "edit";
  /** Обовʼязковий для edit. */
  id?: string;
}>();

const emit = defineEmits<{
  created: [master: Master];
  saved: [master: Master];
  notFound: [];
}>();

const { t } = useI18n();

const form = useMasterProfileForm({
  mode: props.mode,
  id: props.id,
  onCreated: (m) => emit("created", m),
  onSaved: (m) => emit("saved", m),
  onNotFound: () => emit("notFound"),
});

const firstNameRef = ref<{ $el: HTMLInputElement } | null>(null);
const lastNameRef = ref<{ $el: HTMLInputElement } | null>(null);
const middleNameRef = ref<{ $el: HTMLInputElement } | null>(null);
const emailRef = ref<{ $el: HTMLInputElement } | null>(null);
const phoneRef = ref<{ $el: HTMLInputElement } | null>(null);

const fieldRefs: Record<FieldKey, typeof firstNameRef> = {
  firstName: firstNameRef,
  lastName: lastNameRef,
  middleName: middleNameRef,
  email: emailRef,
  phone: phoneRef,
};

/** Форму показуємо у create завжди, у edit — лише коли профіль завантажено. */
const showForm = computed(
  () => props.mode === "create" || form.loadStatus.value === "ready",
);

async function focusInvalid(): Promise<void> {
  const field = form.firstInvalidField.value;
  if (!field) {
    return;
  }
  await nextTick();
  fieldRefs[field].value?.$el.focus();
}

async function onSubmit(): Promise<void> {
  await form.submit();
  await focusInvalid();
}

onMounted(() => {
  if (props.mode === "edit" && props.id) {
    void form.loadForEdit(props.id);
  } else {
    form.initCreate();
  }
});
</script>

<template>
  <div data-testid="master-profile-form">
    <!-- edit: loading -->
    <div
      v-if="mode === 'edit' && form.loadStatus.value === 'loading'"
      data-testid="master-form-loading"
      aria-busy="true"
      class="flex flex-col gap-3"
    >
      <span class="sr-only">{{ t("masters.loading") }}</span>
      <Skeleton class="h-12 w-48" />
      <Skeleton class="h-9 w-full" />
      <Skeleton class="h-9 w-full" />
    </div>

    <!-- edit: load error (network/server) — панель + retry, лишаємось на роуті -->
    <div
      v-else-if="mode === 'edit' && form.loadStatus.value === 'error'"
      data-testid="master-form-load-error"
      class="flex flex-col gap-3"
    >
      <FormAlert
        variant="destructive"
        :message="t('masters.loadError')"
      />
      <div>
        <Button
          variant="outline"
          size="sm"
          @click="id && form.loadForEdit(id)"
        >
          {{ t("masters.retry") }}
        </Button>
      </div>
    </div>

    <!-- ready (create завжди / edit після завантаження) -->
    <form
      v-else-if="showForm"
      id="master-profile-form"
      class="flex flex-col gap-5"
      novalidate
      @submit.prevent="onSubmit"
    >
      <!-- form-level помилка ПІБ (422 invalid_person_name) -->
      <FormAlert
        v-if="form.fieldErrors.form"
        data-testid="master-form-name-error"
        variant="destructive"
        :message="t(form.fieldErrors.form)"
      />

      <!-- ПІБ: ім'я + прізвище поруч на sm+ -->
      <div class="grid gap-5 sm:grid-cols-2">
        <div class="flex flex-col gap-1.5">
          <Label for="master-firstName">
            {{ t("masters.fields.firstName") }}
            <span
              class="text-destructive"
              aria-hidden="true"
            >*</span>
          </Label>
          <Input
            id="master-firstName"
            ref="firstNameRef"
            v-model="form.fields.firstName"
            :invalid="!!form.fieldErrors.firstName"
            aria-required="true"
            :aria-describedby="form.fieldErrors.firstName ? 'master-firstName-err' : undefined"
            autocomplete="given-name"
          />
          <FormAlert
            v-if="form.fieldErrors.firstName"
            id="master-firstName-err"
            variant="destructive"
            :message="t(form.fieldErrors.firstName)"
          />
        </div>

        <div class="flex flex-col gap-1.5">
          <Label for="master-lastName">
            {{ t("masters.fields.lastName") }}
            <span
              class="text-destructive"
              aria-hidden="true"
            >*</span>
          </Label>
          <Input
            id="master-lastName"
            ref="lastNameRef"
            v-model="form.fields.lastName"
            :invalid="!!form.fieldErrors.lastName"
            aria-required="true"
            :aria-describedby="form.fieldErrors.lastName ? 'master-lastName-err' : undefined"
            autocomplete="family-name"
          />
          <FormAlert
            v-if="form.fieldErrors.lastName"
            id="master-lastName-err"
            variant="destructive"
            :message="t(form.fieldErrors.lastName)"
          />
        </div>
      </div>

      <!-- по батькові -->
      <div class="flex flex-col gap-1.5">
        <Label for="master-middleName">
          {{ t("masters.fields.middleName") }}
          <span class="text-xs font-normal text-muted-foreground">
            {{ t("masters.fields.hint.middleName") }}
          </span>
        </Label>
        <Input
          id="master-middleName"
          ref="middleNameRef"
          v-model="form.fields.middleName"
          :invalid="!!form.fieldErrors.middleName"
          :aria-describedby="form.fieldErrors.middleName ? 'master-middleName-err' : undefined"
          autocomplete="additional-name"
        />
        <FormAlert
          v-if="form.fieldErrors.middleName"
          id="master-middleName-err"
          variant="destructive"
          :message="t(form.fieldErrors.middleName)"
        />
      </div>

      <!-- email -->
      <div class="flex flex-col gap-1.5">
        <Label for="master-email">{{ t("masters.fields.email") }}</Label>
        <Input
          id="master-email"
          ref="emailRef"
          v-model="form.fields.email"
          type="email"
          inputmode="email"
          :invalid="!!form.fieldErrors.email"
          :aria-describedby="form.fieldErrors.email ? 'master-email-err' : undefined"
          autocomplete="email"
        />
        <FormAlert
          v-if="form.fieldErrors.email"
          id="master-email-err"
          variant="destructive"
          :message="t(form.fieldErrors.email)"
        />
      </div>

      <!-- телефон -->
      <div class="flex flex-col gap-1.5">
        <Label for="master-phone">{{ t("masters.fields.phone") }}</Label>
        <Input
          id="master-phone"
          ref="phoneRef"
          v-model="form.fields.phone"
          type="tel"
          inputmode="tel"
          :invalid="!!form.fieldErrors.phone"
          :aria-describedby="form.fieldErrors.phone ? 'master-phone-err' : undefined"
          autocomplete="tel"
        />
        <FormAlert
          v-if="form.fieldErrors.phone"
          id="master-phone-err"
          variant="destructive"
          :message="t(form.fieldErrors.phone)"
        />
      </div>

      <!-- дії (desktop-only; mobile — PageCTA нижче) -->
      <div
        data-testid="master-form-actions-inline"
        class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end max-md:hidden"
      >
        <Button
          as-child
          variant="outline"
          class="max-sm:w-full"
        >
          <RouterLink :to="{ name: 'masters' }">
            {{ t("masters.actions.cancel") }}
          </RouterLink>
        </Button>
        <Button
          type="submit"
          class="max-sm:w-full"
          :loading="form.submitStatus.value === 'saving'"
        >
          {{ mode === "create" ? t("masters.actions.create") : t("masters.actions.save") }}
        </Button>
      </div>
    </form>

    <!-- mobile docked bar (form-association: triggers <form id="master-profile-form"> from outside DOM) -->
    <PageCTA v-if="showForm">
      <Button
        as-child
        variant="outline"
      >
        <RouterLink :to="{ name: 'masters' }">
          {{ t("masters.actions.cancel") }}
        </RouterLink>
      </Button>
      <Button
        type="submit"
        form="master-profile-form"
        :loading="form.submitStatus.value === 'saving'"
      >
        {{ mode === "create" ? t("masters.actions.create") : t("masters.actions.save") }}
      </Button>
    </PageCTA>
  </div>
</template>
